import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { useNavigate } from 'react-router';
import { IoIosClose } from 'react-icons/io';

import Button from 'components/ui/button';
import { ModalContext } from 'context/ModalContext';
import { getStorageItem, setStorageItem } from 'utils/storage';

interface IProps {
  tasks: any[] | undefined;
}

const useFocusTrap = (ref: React.RefObject<HTMLDivElement>, isOpen: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !ref.current) return;
      event.preventDefault();

      const focusableElements = ref.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusableElements || focusableElements.length === 0) return;

      const focusableArray = Array.from(focusableElements) as HTMLElement[];
      const currentIndex = focusableArray.indexOf(document.activeElement as HTMLElement);

      let nextIndex: number;
      if (event.shiftKey) {
        nextIndex = currentIndex <= 0 ? focusableArray.length - 1 : currentIndex - 1;
      } else {
        nextIndex = currentIndex >= focusableArray.length - 1 ? 0 : currentIndex + 1;
      }

      focusableArray[nextIndex]?.focus();
    };

    const timeoutId = setTimeout(() => {
      if (ref.current) {
        const firstFocusable = ref.current.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement;
        firstFocusable?.focus();
      }
    }, 50);

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, ref, onClose]);
};

export function TasksPendingApprovalParticipantsModal({ tasks }: IProps) {
  const navigate = useNavigate();

  const modalID = 'tasks-pending-approval-participants-modal';

  const { openModal, closeModal } = useContext(ModalContext);
  const [modalIsOpen, setIsOpen] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);

  ReactModal.setAppElement('#modal');

  function handleCloseModal() {
    //
  }

  function afterOpenModal() {
    //
  }

  const saveReadNotification = useCallback(() => {
    const storedItem = getStorageItem('participant_notifications_read');
    let newStoredItem = [];

    if (Array.isArray(storedItem) && storedItem.length) {
      newStoredItem = [...storedItem, ...(tasks as any[]).map(x => x._id)];
    } else {
      newStoredItem = (tasks as any[]).map(x => x._id);
    }

    setStorageItem('participant_notifications_read', newStoredItem);
  }, [tasks]);

  const handleFocusTrapClose = useCallback(() => {
    saveReadNotification();
    closeModal();
  }, [closeModal, saveReadNotification]);

  useFocusTrap(modalContentRef, modalIsOpen, handleFocusTrapClose);

  useEffect(() => {
    setIsOpen(openModal === modalID && !!tasks?.length);
  }, [openModal, tasks]);

  if (!Array.isArray(tasks) || !tasks?.length) {
    return <></>;
  }

  return (
    <>
      <ReactModal
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={true}
        isOpen={modalIsOpen}
        onAfterOpen={afterOpenModal}
        onRequestClose={handleCloseModal}
        contentLabel="Example Modal"
        portalClassName="relative z-50"
        overlayClassName="fixed z-100 top-0 left-0 w-full h-full bg-black bg-opacity-80 flex flex-col items-center justify-center"
        contentElement={() => (
          <div
            ref={modalContentRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="tasks-pending-approval-participants-modal-title"
          >
            <div className="bg-oxford-blue relative ring-2 ring-electric-blue text-white rounded-xl py-8 px-16 block space-y-8 max-w-[576px]">
              <Button
                color="primary"
                className="absolute -right-5 -top-5  rounded-r-full rounded-l-full w-10 h-10 p-0 flex justify-center items-center"
                onClick={() => [saveReadNotification(), closeModal()]}
                aria-label="Fechar modal"
              >
                <IoIosClose color="white" size={30} aria-hidden="true" />
              </Button>

              <h2 tabIndex={0} id="tasks-pending-approval-participants-modal-title" className="text-yellow text-lg block text-center !m-0 tracking-tight">
                Autorização de participação em Realização Destaque
              </h2>
              <p tabIndex={0} className="m-0 p-0 text-base block font-thin tracking-tight">
                O seu prefixo tem <strong>Realização Destaque com mais de 20 participantes pendente</strong> de autorização.
              </p>
              <p tabIndex={0} className="m-0 p-0 text-base block font-thin tracking-tight">
                OBS.: Caso queira autorizar a participação depois, basta acessar a página{' '}
                <strong>Minha equipe {'>'} Avaliar número de participantes em realização</strong>. Lá você encontrará o card da
                realização sinalizado pelo status e pronto para autorizar ou recusar a quantidade de participantes na realização.
              </p>
              <div className="flex justify-center">
                <Button
                  className="!py-2 !px-3 !text-sm !uppercase !leading-none"
                  onClick={() => [saveReadNotification(), navigate('/minha-equipe/participantes-da-realizacao')]}
                >
                  VER REALIZAÇÃO
                </Button>
              </div>
            </div>
          </div>
        )}
      />
    </>
  );
}


